import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ShieldCheck, AlertTriangle, Info, CheckCircle2, Clock,
  Mic, Camera, BookmarkCheck, Zap, Home, Download, ArrowRight, Copy,
  WifiOff, Wifi, History, FileStack, Stethoscope, Plus, Trash2,
} from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import { SectionHeading, Grid, TextInput, SelectInput } from "@/components/wizard/FormKit";
import TipStrip from "@/components/wizard/TipStrip";

/**
 * `/dev/components` — a single-page gallery of every custom Avida UI primitive
 * with live renders + usage snippets. Not linked from the main app; engineers
 * reach it directly. Lets us iterate tone/spacing without opening the 500-line
 * wizard screens.
 *
 * Organised by category so a nurse-designer review feels like a "catalog"
 * rather than a grep through a codebase.
 */

// ---------- Shared helpers ----------
function Section({ eyebrow, title, description, children }) {
  return (
    <section className="mb-16" data-testid={`pg-section-${eyebrow?.toLowerCase()}`}>
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">{eyebrow}</div>
        <h2 className="font-display text-2xl font-semibold text-[#0B1E3A] mt-1 tracking-tight">{title}</h2>
        {description && <p className="text-[14px] text-[#3A5275] mt-1 max-w-3xl">{description}</p>}
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="rounded-2xl border border-[#D6E3EF] bg-white">
      <div className="px-5 py-3 border-b border-[#E7EEF3] flex items-center justify-between">
        <div>
          <div className="text-[12.5px] font-semibold text-[#0B1E3A]">{label}</div>
          {hint && <div className="text-[11.5px] text-[#3A5275] mt-0.5">{hint}</div>}
        </div>
      </div>
      <div className="p-6 bg-[#FAFBFD] rounded-b-2xl flex flex-wrap items-start gap-4">
        {children}
      </div>
    </div>
  );
}

function Swatch({ name, hex, fg = "#FFFFFF" }) {
  return (
    <div className="w-[124px]">
      <div
        className="h-16 rounded-lg border border-[#D6E3EF] flex items-end justify-start p-2"
        style={{ background: hex, color: fg }}
      >
        <span className="text-[11px] font-mono opacity-90">{hex}</span>
      </div>
      <div className="text-[11.5px] text-[#1E2E4A] mt-1 font-semibold">{name}</div>
    </div>
  );
}

// ---------- Page ----------
export default function ComponentPlayground() {
  const [textValue, setTextValue] = useState("");
  const [selectValue, setSelectValue] = useState("");

  return (
    <main className="min-h-screen bg-paper px-6 md:px-12 py-10" data-testid="playground">
      {/* Header */}
      <header className="max-w-[1200px] mx-auto mb-12 flex items-start justify-between gap-4">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-[12px] text-[#3A5275] mb-3 hover:text-[#0A3D79]">
            <Home className="w-3.5 h-3.5" /> Back to landing
          </Link>
          <div className="eyebrow mb-3">Avida Health · Design System</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0B1E3A] tracking-tight">
            Component <span className="yellow-underline">playground</span>
          </h1>
          <p className="text-[14.5px] text-[#3A5275] mt-3 max-w-2xl">
            Living catalog of every Avida Health UI primitive — brand, typography, buttons, inputs, badges,
            layout, feedback. Iterate here first; the wizard screens reuse everything.
          </p>
        </div>
        <AvidaLogo size="lg" />
      </header>

      <div className="max-w-[1200px] mx-auto">
        {/* Brand ------------------------------------------------- */}
        <Section eyebrow="Brand" title="Logo & palette" description="Anchors every Avida screen. Adapt size, never re-colour.">
          <Row label="AvidaLogo sizes" hint="Accepts sm / md / lg">
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center gap-1">
                <AvidaLogo size="sm" />
                <span className="text-[11px] text-[#3A5275]">sm</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <AvidaLogo size="md" />
                <span className="text-[11px] text-[#3A5275]">md</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <AvidaLogo size="lg" />
                <span className="text-[11px] text-[#3A5275]">lg</span>
              </div>
            </div>
          </Row>

          <Row label="Palette">
            <Swatch name="Night (chrome)" hex="#030E20" />
            <Swatch name="Midnight" hex="#051A36" />
            <Swatch name="Navy deep" hex="#072A54" />
            <Swatch name="Navy (primary)" hex="#0A3D79" />
            <Swatch name="Navy mid" hex="#1565B8" />
            <Swatch name="Sky" hex="#1FA0E6" />
            <Swatch name="Yellow" hex="#FFDA0B" fg="#072A54" />
            <Swatch name="Yellow dark" hex="#DBB90A" fg="#072A54" />
            <Swatch name="Cream" hex="#FBF9F2" fg="#072A54" />
            <Swatch name="Paper" hex="#F7FAFC" fg="#072A54" />
            <Swatch name="Ink" hex="#0B1E3A" />
            <Swatch name="Mist" hex="#D6E3EF" fg="#072A54" />
          </Row>

          <Row label="Accent chip" hint="avida-accent-chip — cadmium-yellow tab, used on footer + branding pages">
            <span className="avida-accent-chip">Avida</span>
            <span className="avida-accent-chip">Wisconsin</span>
            <span className="avida-accent-chip">RN</span>
          </Row>
        </Section>

        {/* Typography -------------------------------------------- */}
        <Section eyebrow="Typography" title="Hierarchy" description="Two families: display (serif) for headings, sans for body. Eyebrows use tracked uppercase.">
          <Row label="H1 · display · 4xl → 6xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0B1E3A] tracking-tight">Tablet-first clinical intake</h1>
          </Row>
          <Row label="H2 · display · 2xl → 3xl">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0B1E3A]">Non-Skilled Assessment</h2>
          </Row>
          <Row label="Eyebrow · 11px · tracked">
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">Step 2 · Section B</span>
          </Row>
          <Row label="Body · 14-15px">
            <p className="text-[#1E2E4A] text-[14.5px] leading-relaxed max-w-xl">
              Body copy throughout the app sits at 14.5px with relaxed leading. Muted copy uses the slate `#3A5275`, headings use the ink `#0B1E3A`.
            </p>
          </Row>
          <Row label="Small / fine print · 11.5px">
            <span className="text-[11.5px] text-[#3A5275]">DHS 107.112 compliant dual-signature workflow</span>
          </Row>
          <Row label="Mono / field-path · 11-12px">
            <span className="text-[11.5px] font-mono text-[#1E2E4A]">adl.bathing.notes</span>
            <span className="text-[10.5px] font-mono uppercase tracking-[0.08em] bg-[#0f3b5f] text-white px-1.5 py-0.5 rounded">DHS-C-COMMENT</span>
          </Row>
        </Section>

        {/* Buttons ---------------------------------------------- */}
        <Section eyebrow="Buttons" title="Calls to action" description="Three tiers: primary (navy), accent (yellow), ghost (outline). Disabled states dim to 40%.">
          <Row label="Primary">
            <button className="btn-primary">Begin clinical intake <ArrowRight className="w-4 h-4" /></button>
            <button className="btn-primary" disabled>Disabled</button>
            <button className="btn-primary !h-9">Compact (!h-9)</button>
          </Row>
          <Row label="Accent (yellow)">
            <button className="btn-accent">Generate & Fax <Sparkles className="w-4 h-4" /></button>
          </Row>
          <Row label="Ghost (outline)">
            <button className="btn-ghost"><Copy className="w-4 h-4" /> Copy from last visit</button>
            <button className="btn-ghost" disabled>Disabled ghost</button>
          </Row>
          <Row label="Icon-only">
            <button className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-[#3A5275] hover:bg-[#E7F1FB]">
              <Download className="w-4 h-4" />
            </button>
            <button className="text-[#c05746] p-2 hover:bg-[#fbeae5] rounded-md">
              <Trash2 className="w-4 h-4" />
            </button>
          </Row>
        </Section>

        {/* Form inputs ------------------------------------------ */}
        <Section eyebrow="Forms" title="Inputs" description="Native HTML elements styled via index.css utility classes. Never build custom inputs — reuse these.">
          <Row label="TextInput (FormKit)">
            <div className="w-full max-w-md">
              <TextInput label="Member ID" placeholder="10-digit ForwardHealth ID"
                value={textValue} onChange={setTextValue} testid="demo-text" />
            </div>
          </Row>
          <Row label="SelectInput (FormKit)">
            <div className="w-full max-w-md">
              <SelectInput label="Sex" value={selectValue} onChange={setSelectValue}
                options={[{ value: "Female", label: "Female" }, { value: "Male", label: "Male" }]}
                testid="demo-select" />
            </div>
          </Row>
          <Row label="Raw tb-input">
            <input className="tb-input max-w-md" placeholder="Raw .tb-input utility class" />
          </Row>
          <Row label="Textarea (.tb-textarea)">
            <textarea rows={3} className="tb-textarea max-w-2xl" placeholder="Multi-line narrative goes here…" />
          </Row>
        </Section>

        {/* Badges & pills --------------------------------------- */}
        <Section eyebrow="Badges & pills" title="Status signals" description="Tight, bordered pills carry state at-a-glance. Color always matches severity.">
          <Row label="Status pills (SubmissionLog)">
            <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border bg-[#d9e6df] text-[#0A3D79] border-[#0A3D79]">
              <CheckCircle2 className="w-2.5 h-2.5" /> Approved
            </span>
            <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border bg-[#E7F1FB] text-[#0A3D79] border-[#1FA0E6]">
              <Clock className="w-2.5 h-2.5" /> Submitted
            </span>
            <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border bg-[#E7F1FB] text-[#3A5275] border-[#C1D1E1]">
              <Clock className="w-2.5 h-2.5" /> Generated
            </span>
            <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border bg-[#fbeae5] text-[#c05746] border-[#c05746]">
              <AlertTriangle className="w-2.5 h-2.5" /> Denied
            </span>
          </Row>
          <Row label="DHS compliance badges (Sidebar)">
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fbeae5] text-[#c05746]">
              <AlertTriangle className="w-2.5 h-2.5" />2
            </span>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fbf2d8] text-[#a07a27]">
              <AlertTriangle className="w-2.5 h-2.5" />1
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#B7CADA] bg-[#E7F1FB] text-[#0A3D79] text-[11.5px] font-semibold uppercase tracking-[0.1em] px-3 py-1">
              <ShieldCheck className="w-3.5 h-3.5" /> DHS-compliant on this step
            </span>
          </Row>
          <Row label="Offline/online (network)">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#DBB90A] bg-[#fbf2d8] px-4 py-1.5 text-[12.5px] font-semibold text-[#a07a27]">
              <WifiOff className="w-3.5 h-3.5" />
              You're offline — drafts are saving locally.
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B7CADA] bg-[#E7F1FB] px-4 py-1.5 text-[12.5px] font-semibold text-[#0A3D79]">
              <Wifi className="w-3.5 h-3.5" /> Back online · AI features available
            </div>
          </Row>
        </Section>

        {/* Cards & layout --------------------------------------- */}
        <Section eyebrow="Layout" title="Cards & section heads" description="Every wizard step is a stack of `.card-surface` sections, each opened by a `<SectionHeading>`.">
          <Row label=".card-surface + SectionHeading">
            <section className="card-surface fade-up w-full">
              <SectionHeading eyebrow="Step 5 · Section J" title="Diagnoses (ICD-10)"
                description="Start typing a condition or code — we'll find the match." />
              <Grid cols={2}>
                <TextInput label="ICD-10" placeholder="G20" testid="demo-icd" onChange={() => {}} value="" />
                <TextInput label="Onset" placeholder="YYYY-MM" testid="demo-onset" onChange={() => {}} value="" />
              </Grid>
            </section>
          </Row>
          <Row label="Resume card (Landing.jsx)">
            <div className="w-full rounded-3xl p-5 border border-[#B7CADA] bg-gradient-to-br from-[#E7F1FB] to-[#f4f6f1]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl bg-[#0A3D79] text-[#F7FAFC] flex items-center justify-center">
                  <History className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">Resume where you left off</div>
                  <div className="font-display text-lg font-semibold text-[#0B1E3A]">Draft for Jane Doe · Avida</div>
                </div>
                <button className="btn-primary !h-8">Resume <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          </Row>
        </Section>

        {/* Feedback --------------------------------------------- */}
        <Section eyebrow="Feedback" title="Banners, tips, strips" description="Surface guidance and compliance status without shouting.">
          <Row label="TipStrip" hint="Three tappable cards, per-step">
            <TipStrip
              items={[
                { type: "tip",      text: "Dictate any narrative: tap the blue Mic and speak — Whisper does the rest." },
                { type: "reminder", text: "Take a photo of the med list on Step 5 and import multiple rows at once." },
                { type: "dhs",      text: "C-level ADLs require a comment. The wizard blocks Next until you add one." },
              ]}
              testid="demo-tips"
            />
          </Row>
          <Row label="DhsStepBanner — error state">
            <div className="w-full rounded-xl border border-[#e2b6a9] bg-[#fbeae5] text-[#c05746]">
              <div className="flex items-center gap-2 px-4 pt-3.5">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="text-[11.5px] uppercase tracking-[0.12em] font-semibold">DHS compliance · this step</span>
                <span className="text-[12px] font-semibold">2 errors</span>
              </div>
              <div className="px-4 py-2.5 flex items-center gap-3">
                <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#c05746]"></span>
                <div className="flex-1 text-[#0B1E3A]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-[0.08em] bg-[#0f3b5f] text-white px-1.5 py-0.5 rounded">DHS-C-COMMENT</span>
                    <span className="text-[11.5px] font-mono text-[#1E2E4A]">adl.bathing.notes</span>
                  </div>
                  <div className="text-[13.5px] leading-snug mt-0.5">Bathing scored C — comment required.</div>
                </div>
                <button className="inline-flex items-center gap-1 rounded-md border border-[#0A3D79] bg-white hover:bg-[#E7F1FB] text-[#0A3D79] text-[11.5px] font-semibold px-2.5 py-1">
                  Jump to field <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </Row>
          <Row label="Readiness progress bar (Landing)">
            <div className="w-full max-w-2xl">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#DBB90A]" />
                  <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[#3A5275]">DHS readiness</span>
                </div>
                <span className="text-[13px] font-semibold text-[#0B1E3A]">9 of 10 steps compliant</span>
              </div>
              <div className="h-2 rounded-full bg-[#D6E3EF] overflow-hidden">
                <div className="h-full rounded-full bg-[#DBB90A]" style={{ width: "90%" }} />
              </div>
            </div>
          </Row>
        </Section>

        {/* Sidebar (preview) ------------------------------------ */}
        <Section eyebrow="Wizard progress" title="Sidebar step row">
          <Row label="Active + done + badge">
            <div className="relative w-[320px] bg-white border border-[#D6E3EF] rounded-xl p-4">
              <div className="absolute left-[27px] top-5 bottom-5 w-px bg-[#D6E3EF]" />
              <div className="flex items-start gap-4 py-2">
                <CheckCircle2 className="w-6 h-6 text-[#0A3D79] relative z-10" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="text-[14px] font-semibold text-[#1E2E4A]">1. Agency & Client</div>
                  <div className="text-[12px] text-[#3A5275] mt-0.5">Facility + member demographics.</div>
                </div>
              </div>
              <div className="flex items-start gap-4 py-2">
                <div className="relative z-10">
                  <div className="w-6 h-6 rounded-full bg-[#0A3D79] flex items-center justify-center">
                    <div className="w-2 h-2 bg-[#F7FAFC] rounded-full" />
                  </div>
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-[#c05746]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <div className="text-[14px] font-semibold text-[#0B1E3A]">2. ADL Assessment</div>
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fbeae5] text-[#c05746]">
                      <AlertTriangle className="w-2.5 h-2.5" />2
                    </span>
                  </div>
                  <div className="text-[12px] text-[#3A5275] mt-0.5">PCST levels A-G, days/week, notes.</div>
                </div>
              </div>
            </div>
          </Row>
        </Section>

        {/* Utility ---------------------------------------------- */}
        <Section eyebrow="Icons" title="Most-used icons" description="All icons from `lucide-react`. No emoji, no custom SVGs — consistency is the feature.">
          <Row label="Common set">
            {[
              [Sparkles, "Sparkles"], [ShieldCheck, "ShieldCheck"], [AlertTriangle, "AlertTriangle"],
              [Info, "Info"], [CheckCircle2, "CheckCircle2"], [Clock, "Clock"],
              [Mic, "Mic"], [Camera, "Camera"], [BookmarkCheck, "BookmarkCheck"],
              [Zap, "Zap"], [Download, "Download"], [ArrowRight, "ArrowRight"],
              [Copy, "Copy"], [History, "History"], [Stethoscope, "Stethoscope"],
              [FileStack, "FileStack"], [Plus, "Plus"], [Trash2, "Trash2"],
            ].map(([Icon, name]) => (
              <div key={name} className="w-[112px] flex flex-col items-center gap-1.5 rounded-lg bg-white border border-[#D6E3EF] p-3">
                <Icon className="w-5 h-5 text-[#0A3D79]" />
                <span className="text-[10.5px] font-mono text-[#3A5275]">{name}</span>
              </div>
            ))}
          </Row>
        </Section>

        <footer className="mt-16 pt-6 border-t border-[#D6E3EF] text-[12px] text-[#3A5275]">
          Not wired into navigation. Reach via <code className="font-mono text-[#0B1E3A]">/dev/components</code>. Edits here don't ship until you lift them into real screens.
        </footer>
      </div>
    </main>
  );
}
